import React from 'react';
import { Box, Tooltip } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { getCandidateVideoInterviewUrl, resumeFileUrl } from '@constants';
import { CandidatesWithPosition } from '@types';
import { NotProvided } from 'components/common/NotProvided/NotProvided';
import { ResumeButton } from './ResumeButton';
import { TILE, VideoIntroThumb } from './VideoIntroThumb';

const EmptyTile: React.FC<{ title: string }> = ({ title }) => (
  <Tooltip title={title} placement="top">
    <Box
      component="span"
      sx={(theme) => ({
        ...TILE,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        borderRadius: 1,
        backgroundColor: theme.palette.highlight.neutral,
        color: theme.palette.text.lightGrey,
        fontSize: '0.75rem',
      })}
    >
      n/a
    </Box>
  </Tooltip>
);

const CandidateVideoThumb: React.FC<{
  candidate: CandidatesWithPosition;
  onPlay: () => void;
}> = ({ candidate, onPlay }) => {
  if (candidate.videoFileName === undefined)
    return <NotProvided field="video intros" />;

  if (!candidate.videoFileName)
    return <EmptyTile title="No video intro on file" />;

  return (
    <VideoIntroThumb
      name={`${candidate.firstName} ${candidate.lastName}`}
      videoUrlAPI={getCandidateVideoInterviewUrl(candidate.videoFileName)}
      onPlay={onPlay}
    />
  );
};

export const ResumeLink: React.FC<{
  candidate: CandidatesWithPosition;
  label?: string;
}> = ({ candidate, label }) => {
  if (candidate.resumeName === undefined)
    return <NotProvided field="resumes" />;

  if (!candidate.resumeName)
    return (
      <Tooltip title="No resume on file" placement="top">
        <Box
          component="span"
          sx={(theme) => ({
            display: 'grid',
            placeItems: 'center',
            width: '1.75rem',
            height: '1.75rem',
            flexShrink: 0,
            color: theme.palette.background.dividerStrong,
          })}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: '1.25rem' }} />
        </Box>
      </Tooltip>
    );

  return (
    <ResumeButton
      label={label}
      href={resumeFileUrl(candidate.resumeName)}
      name={`${candidate.firstName} ${candidate.lastName}`}
    />
  );
};

export const CandidateMaterials: React.FC<{
  candidate: CandidatesWithPosition;
  showVideo?: boolean;
  onPlay: () => void;
}> = ({ candidate, showVideo = true, onPlay }) => (
  <Box display="flex" alignItems="center" gap={1}>
    {showVideo && <CandidateVideoThumb candidate={candidate} onPlay={onPlay} />}
    <ResumeLink candidate={candidate} />
  </Box>
);
